import React, {Component, Fragment} from 'react';
import {PortalWithState} from 'react-portal';
import produce from 'immer';
import withLifecycles from 'async-lifecycles';
import {
  Button,
  Message,
  Table,
  Icon,
  Pagination,
  Header,
  Form,
  Popup,
  Input,
} from 'semantic-ui-react';
import toastr from 'toastr';
import {connect} from 'react-redux';
import {isBusy, withActions} from 'actionware';
import styled from 'react-emotion';
import {debounce} from 'lodash';

import {fetch} from '../../store/customers';
import customerApi from '../../store/services/customers';
import FormContainer from '../form-components/FormContainer';
import {Spring} from 'react-spring/renderprops';
import {ModalWrapper} from '../modal/ModalWrapper';
import Layout from '../layout/Layout';
import Page from '../layout/Page';
import LoadingContainer from '../layout/LoadingContainer';
import CustomerForm from './CustomerForm';
import {ErrorMessages} from '../form-components/ErrorMessages';

const Styles = styled.div`
  .tableHeader {
    background-color: '#f00';
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .ui.header {
      margin-bottom: 0;
    }
  }
`;

class AllCustomersTable extends Component {
  state = {
    customer: undefined,
    searchTerm: '',
    orderByEpicor: false,
    activePage: 1,
    pageInfo: {},
  };

  componentDidMount() {
    this._loadCustomers(0, '', false);
  }

  _loadCustomers = (pageNumber, searchTerm, orderByEpicor) => {
    withLifecycles(
      () => this.props.fetch(pageNumber, 20, searchTerm, orderByEpicor),
      ({loading, data, error}) => {
        this.setState({activePage: pageNumber + 1});
        this.setState(
          produce(draft => {
            draft.loading = loading;

            if (data && !data.hasErrors) {
              draft.pageInfo = data.result;
            }
          })
        );
      }
    );
  };

  _mutate = cb => {
    this.setState(produce(cb));
  };

  _deleteCustomer = customer => {
    withLifecycles(
      () => customerApi.remove(customer),
      ({loading, data, error}) => {
        this._mutate(draft => {
          draft.loading = loading;

          if (data && !data.hasErrors) {
            toastr.success('Customer Deleted');
            draft.loading = true;
            setTimeout(this._loadCustomers);
          }

          if (error && error.errors) {
            toastr.error('Unable to delete customer');
          }
        });
      }
    );
  };

  _handleTextChanged = (event, {name, value}) => {
    this._mutate(draft => {
      draft.customer[name] = value;
    });
  };

  _deleteCustomer = async customer => {
    try {
      await customerApi.remove(customer);
      toastr.info('Customer removed');
      this._loadCustomers(0, this.state.searchTerm, this.state.orderByEpicor);

      this._closePortal();
    } catch (error) {
      toastr.warning('Error removing customer');
    }
  };

  _save = () => {
    withLifecycles(
      () => customerApi.update(this.state.customer),
      ({loading, data, error}) => {
        this.setState(
          produce(draft => {
            draft.saving = loading;
            draft.error = error;

            if (data && !data.hasErrors) {
              this._loadCustomers(
                0,
                this.state.searchTerm,
                this.state.orderByEpicor
              );
              toastr.success('Customer Updated');
              this._closePortal();
            }
          })
        );
      }
    );
  };

  _showDetail = customer => {
    this._openPortal();
    this.setState({customer});
  };

  render() {
    const {customers} = this.props;
    const {searchTerm, activePage, pageInfo} = this.state;
    return (
      <FormContainer loading={this.props.loading}>
        <Styles>
          <div className="tableHeader">
            <Header as="h1">Customers</Header>
            <Input
              placeholder="Search Customers..."
              onChange={debounce((e, searchTerm) => {
                this.setState({searchTerm: searchTerm.value});
                this._loadCustomers(
                  0,
                  this.state.searchTerm,
                  this.state.orderByEpicor
                );
              }, 400)}
            />
          </div>
        </Styles>
        {pageInfo.pageCount === 0 && <Message info>No Customers</Message>}
        {pageInfo.pageCount > 0 && (
          <PortalWithState closeOnEsc>
            {({openPortal, closePortal, portal}) => {
              this._closePortal = closePortal;
              this._openPortal = openPortal;
              return (
                <Fragment>
                  <Table basic="very" selectable>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell />

                        <Table.HeaderCell
                          style={{cursor: 'pointer'}}
                          onClick={() => {
                            this.setState({orderByEpicor: false}, () =>
                              this._loadCustomers(
                                0,
                                searchTerm,
                                this.state.orderByEpicor
                              )
                            );
                          }}
                        >
                          Name {!this.state.orderByEpicor && '▼'}
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          style={{cursor: 'pointer'}}
                          onClick={() =>
                            this.setState({orderByEpicor: true}, () => {
                              this._loadCustomers(
                                0,
                                searchTerm,
                                this.state.orderByEpicor
                              );
                            })
                          }
                        >
                          Epicor Customer Number{' '}
                          {this.state.orderByEpicor && '▼'}
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {customers.map(customer => this._renderRow(customer))}
                    </Table.Body>
                  </Table>
                  <Pagination
                    activePage={activePage}
                    onPageChange={async (e, paginationInfo) => {
                      await this._loadCustomers(
                        paginationInfo.activePage - 1,
                        this.state.searchTerm
                      );
                    }}
                    ellipsisItem={{
                      content: <Icon name="ellipsis horizontal" />,
                      icon: true,
                    }}
                    firstItem={{
                      content: <Icon name="angle double left" />,
                      icon: true,
                    }}
                    lastItem={{
                      content: <Icon name="angle double right" />,
                      icon: true,
                    }}
                    prevItem={{content: <Icon name="angle left" />, icon: true}}
                    nextItem={{
                      content: <Icon name="angle right" />,
                      icon: true,
                    }}
                    totalPages={pageInfo.pageCount}
                  />
                  {portal(this._renderDetailModal())}
                </Fragment>
              );
            }}
          </PortalWithState>
        )}
      </FormContainer>
    );
  }

  _renderRow = customer => (
    <Table.Row key={customer.id}>
      <Table.Cell collapsing>
        <Button basic icon onClick={() => this._showDetail(customer)}>
          <Icon name="edit" />
        </Button>
      </Table.Cell>
      <Table.Cell>{customer.name}</Table.Cell>
      <Table.Cell>{customer.epicorId}</Table.Cell>
    </Table.Row>
  );

  _renderDetailModal = () => {
    const {customer, error = {}} = this.state;
    return (
      <Spring from={{right: '-100%'}} to={{right: '0%'}}>
        {styles => (
          <ModalWrapper style={styles}>
            <Layout className="page-wrapper">
              <Header className="page-header">
                <Page.HeaderWithActions title="Edit Customer">
                  <Button
                    icon="close"
                    size="mini"
                    onClick={this._closePortal}
                  />
                </Page.HeaderWithActions>
              </Header>
              <Layout.Fill scroll padded>
                <LoadingContainer hideContentWhenLoading>
                  <Form onSubmit={this._save}>
                    <CustomerForm
                      customer={customer}
                      handleTextChanged={this._handleTextChanged}
                    />
                    <ErrorMessages error={error} />
                    <Button type="Submit" primary loading={this.state.saving}>
                      Update
                    </Button>
                    <Button basic onClick={this._closePortal}>
                      Cancel
                    </Button>
                    <Popup
                      trigger={
                        <Button basic icon floated="right" type="button">
                          <Icon name="trash" color="red" />
                        </Button>
                      }
                      content={
                        <Button
                          type="button"
                          color="red"
                          content="Are you sure?"
                          onClick={() => this._deleteCustomer(customer)}
                        />
                      }
                      on="click"
                      position="left center"
                    />
                  </Form>
                </LoadingContainer>
              </Layout.Fill>
            </Layout>
          </ModalWrapper>
        )}
      </Spring>
    );
  };
}

const mapStateToProps = ({customers}) => ({
  customers: customers.data,
  loading: isBusy(fetch),
});

const AllCustomersTableWithActions = withActions({fetch})(AllCustomersTable);
export default connect(mapStateToProps)(AllCustomersTableWithActions);
